// @ts-nocheck
import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useAuth0 } from '@auth0/auth0-react';

import { medicalHistoryActions, treatmentPlanActions } from '../../../../../store';
import {
  MedicalHistoryRowSchema,
  MedicalHistorySchema,
  ReferralHistorySchema,
  initialMedicalHistory,
  initialReferralHistory,
} from '../../medical-history/validationSchema';
import { getAllInvalidFieldNames } from '../../../../../helpers';
import { TreatmentPlanPages } from '../../constants/general';

import type { RootState } from '../../../../../store';
import type { IDiagnosis, IMedicalHistory, IReferralHistory } from '../../../../../utils/types';

function useValidateMedicalHistory() {
  const dispatch = useDispatch();
  const { getAccessTokenSilently } = useAuth0();
  const { current: treatmentPlan } = useSelector((state: RootState) => state.treatmentPlans);
  const {
    medicalHistory,
    referralHistory,
    diagnoses,
    treatmentPlanId: currentTPId,
  } = useSelector((state: RootState) => state.medicalHistories);
  const { selected: selectedClient } = useSelector((state: RootState) => state.clients);

  const planId = treatmentPlan?.id;

  const [isValid, setIsValid] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [treatmentPlanId, setTreatmentPlanId] = useState<number>();

  useEffect(() => {
    if (!planId) return;
    setTreatmentPlanId(planId);
  }, [planId]);

  useEffect(() => {
    if (!treatmentPlanId || !selectedClient?.id) return;
    dispatch(
      medicalHistoryActions.updateState({ key: 'triggerValidationsOnPageLoad', data: true }),
    );
    if (currentTPId === treatmentPlanId) {
      const medicalHistoryData = medicalHistory || null;
      const referralHistoryData = referralHistory || null;
      fetchAndValidateData(medicalHistoryData, referralHistoryData);
    } else {
      dispatch(medicalHistoryActions.clearState());
      fetchAndValidateData(null, null);
    }
    dispatch(medicalHistoryActions.updateState({ key: 'treatmentPlanId', data: treatmentPlanId }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [treatmentPlanId, selectedClient]);

  async function fetchAndValidateData(
    medicalHistoryData: IMedicalHistory | null,
    referralHistoryData: IReferralHistory | null,
  ) {
    setIsLoading(true);
    const medicalHistoryPromise = medicalHistoryData
      ? null
      : dispatch(
          medicalHistoryActions.getMedicalHistory({
            getAccessTokenSilently,
            planId: treatmentPlanId,
            forceUpdate: true,
          }),
        );
    const referralHistoryPromise = referralHistoryData
      ? null
      : dispatch(
          medicalHistoryActions.getReferralHistory({
            getAccessTokenSilently,
            planId: treatmentPlanId,
          }),
        );

    const [medHistory, refHistory] = await Promise.all([
      medicalHistoryPromise,
      referralHistoryPromise,
    ]);

    const medicalHistoryResponse = medHistory ? { ...medHistory.payload } : medicalHistoryData;
    const referralHistoryResponse = refHistory ? { ...refHistory.payload } : referralHistoryData;

    let diagnosisList = diagnoses ? diagnoses : [];

    if (medicalHistoryResponse?.id) {
      const response = await dispatch(
        medicalHistoryActions.getDiagnoses({
          getAccessTokenSilently,
          planId: treatmentPlanId,
          medicalHistoryId: medicalHistoryResponse.id,
          forceUpdate: true,
        }),
      );
      // SAFETY: The medical-history thunk declares an IDiagnosis[] response.
      diagnosisList = response.payload as IDiagnosis[];
    } else {
      dispatch(medicalHistoryActions.updateState({ key: 'diagnoses', data: [] }));
    }
    setIsLoading(false);
    validate(medicalHistoryResponse, referralHistoryResponse, diagnosisList);
  }

  const validate = (
    medicalHistoryData: IMedicalHistory,
    referralHistoryData: IReferralHistory,
    diagnosisList: IDiagnosis[],
  ) => {
    let medicalHistoryErrors: Record<string, boolean> = {};
    let referralHistoryErrors: Record<string, boolean> = {};

    const medicalHistoryVersion = treatmentPlan?.resource_versions?.medical_history;

    if (!medicalHistoryData?.id) {
      medicalHistoryErrors = getAllInvalidFieldNames(MedicalHistorySchema, {
        ...initialMedicalHistory,
        ...medicalHistoryData,
        medical_history_version: medicalHistoryVersion,
      });
    } else {
      const data = {
        ...medicalHistoryData,
        medical_history_version: medicalHistoryVersion,
      };
      delete data.id;
      delete data.diagnoses;
      medicalHistoryErrors = getAllInvalidFieldNames(MedicalHistorySchema, data);
    }

    if (!referralHistoryData?.id) {
      referralHistoryErrors = getAllInvalidFieldNames(ReferralHistorySchema, {
        ...initialReferralHistory,
        ...referralHistoryData,
      });
    } else {
      referralHistoryErrors = getAllInvalidFieldNames(ReferralHistorySchema, referralHistoryData);
    }

    let errorCount = Object.keys(medicalHistoryErrors).length;
    errorCount += Object.keys(referralHistoryErrors).length;

    if (diagnosisList && diagnosisList.length) {
      diagnosisList.forEach((diagnosis: IDiagnosis) => {
        const data = { ...diagnosis };
        delete data.id;
        const errorObject = getAllInvalidFieldNames(MedicalHistoryRowSchema, data);
        errorCount += Object.keys(errorObject).length;
      });
    }
    setIsValid(errorCount === 0 ? true : false);
    dispatch(
      treatmentPlanActions.updateBadge({
        [TreatmentPlanPages.MedInfo]: errorCount,
      }),
    );
  };

  return isValid && !isLoading;
}

export default useValidateMedicalHistory;
